// Le festività nazionali italiane, le giornate rosse del calendario (richiesta del product owner del 1° ottobre 2026):
// un'azienda nuova le trova già fra le chiusure e le Impostazioni le aggiungono un anno alla volta. Sono date di
// calendario «YYYY-MM-DD», uguali in ogni fuso, e ognuna ha il suo nome, che diventa il motivo della chiusura (un testo
// su una riga entro 40 caratteri, come vuole `workingCalendarSchema`). La Pasqua si calcola (calendario gregoriano),
// Pasquetta è il giorno dopo; le altre hanno una data fissa. Dal 2026 c'è anche San Francesco d'Assisi, il 4 ottobre
// (legge 8 ottobre 2025, n. 151). Le feste del patrono non ci sono: dipendono dalla città (questione aperta 123).
// Il modulo non dipende da nient'altro del dominio, così l'interfaccia lo importa da `@gioia365/domain/italian-holidays`
// senza portarsi nel bundle il dataset dei comuni.
// Il calcolo della Pasqua vale per il calendario gregoriano; le date ISO hanno l'anno di quattro cifre.
const FIRST_YEAR = 1583;
const LAST_YEAR = 9999;
// Il primo anno con San Francesco fra le festività (legge 8 ottobre 2025, n. 151, in vigore dal 1° gennaio 2026).
const SAN_FRANCESCO_SINCE = 2026;
const FIXED = [
    [1, 1, "Capodanno"],
    [1, 6, "Epifania"],
    [4, 25, "Festa della Liberazione"],
    [5, 1, "Festa del Lavoro"],
    [6, 2, "Festa della Repubblica"],
    [8, 15, "Ferragosto"],
    [11, 1, "Ognissanti"],
    [12, 8, "Immacolata Concezione"],
    [12, 25, "Natale"],
    [12, 26, "Santo Stefano"],
];
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
function checkYear(year) {
    if (!Number.isInteger(year) || year < FIRST_YEAR || year > LAST_YEAR) {
        throw new RangeError(`The year must be a whole number from ${FIRST_YEAR} to ${LAST_YEAR}: ${year}`);
    }
}
const pad = (value) => String(value).padStart(2, "0");
const isoOf = (year, month, day) => `${String(year).padStart(4, "0")}-${pad(month)}-${pad(day)}`;
/** Il giorno dopo una data, passando al mese seguente quando serve. */
function nextDay(year, month, day) {
    const next = new Date(Date.UTC(year, month - 1, day + 1));
    return isoOf(next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate());
}
/** L'anno di una data «YYYY-MM-DD» che esiste davvero (niente 30 febbraio né mese 13). */
function yearOfDate(value) {
    const match = ISO_DATE.exec(value);
    if (match) {
        const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
        const date = new Date(Date.UTC(year, month - 1, day));
        if (date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day)
            return year;
    }
    throw new RangeError(`Not a calendar date: ${value}`);
}
/** La domenica di Pasqua di un anno, «YYYY-MM-DD» (algoritmo gregoriano anonimo, di Meeus, Jones e Butcher). */
export function easterSunday(year) {
    checkYear(year);
    return isoOf(year, ...easterMonthDay(year));
}
function easterMonthDay(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return [month, day];
}
/** Le festività nazionali di un anno, dalla prima all'ultima, una per data. */
export function italianNationalHolidays(year) {
    checkYear(year);
    const [easterMonth, easterDay] = easterMonthDay(year);
    // Data → nomi interi e brevi. Pasqua può cadere il 25 aprile (nel 2038) e Pasquetta pure (nel 2011): la data resta
    // una e il nome le dice tutte e due, in forma breve perché stia nei 40 caratteri del motivo di una chiusura.
    const byDate = new Map();
    const add = (date, name, short = name) => byDate.set(date, [...(byDate.get(date) ?? []), { name, short }]);
    add(isoOf(year, easterMonth, easterDay), "Pasqua");
    add(nextDay(year, easterMonth, easterDay), "Lunedì dell'Angelo (Pasquetta)", "Pasquetta");
    for (const [month, day, name] of FIXED)
        add(isoOf(year, month, day), name);
    if (year >= SAN_FRANCESCO_SINCE)
        add(isoOf(year, 10, 4), "San Francesco d'Assisi");
    return [...byDate]
        .map(([date, names]) => ({ date, name: names.length === 1 ? (names[0]?.name ?? "") : names.map((entry) => entry.short).join(" e ") }))
        .sort((left, right) => left.date.localeCompare(right.date));
}
/**
 * Le festività nazionali da un giorno (compreso) alla fine dell'anno `throughYear`, dalla prima all'ultima. Vuoto se
 * `throughYear` viene prima dell'anno di quel giorno.
 */
export function italianNationalHolidaysFrom(from, throughYear) {
    const year = yearOfDate(from);
    const holidays = [];
    for (let current = year; current <= throughYear; current += 1) {
        holidays.push(...italianNationalHolidays(current).filter((holiday) => holiday.date >= from));
    }
    return holidays;
}
//# sourceMappingURL=italian-holidays.js.map